import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import { useUIStore } from '../../../stores/uiStore';
import { useSSEStore } from '../../../stores/sseStore';
import { usePagination } from '../../../hooks/usePagination';
import { Pagination } from '../../../components/shared/Pagination';
import { StatusBadge } from '../../../components/shared/StatusBadge';
import { EntityLink } from '../../../components/shared/EntityLink';
import {
  Rocket, Search, FileText, Eye, CheckCircle2, XCircle,
  Clock, AlertCircle, Loader2, Package, Trash2, RefreshCw,
  ChevronLeft, ChevronRight, X, Send, Sparkles, Link,
  PenTool, Grid3X3, BarChart3, Filter, Check, Ban,
  TrendingUp, Binoculars, FolderTree, Plus,
  Play, Pause, Settings2, ChevronDown, ChevronUp,
  Globe, Star, Weight, ShoppingCart, Zap, Edit3, Layers
} from 'lucide-react';
import { QUEUE_STATUS_MAP, SITE_FLAGS } from '../shared';
import { StatCard } from '../components/StatCard';
import { EnrichmentProgressPanel } from '../panels/EnrichmentProgressPanel';
import { PublishSettingsPanel } from '../panels/PublishSettingsPanel';
import { useCan, Can } from '../../../lib/rbac';

function QueueTab() {
  const { t } = useTranslation();
  const can = useCan();
  const canEnqueue = can('publisher.enqueue');
  const canDelete = can('publisher.delete');
  const canBulk = canEnqueue || canDelete;
  const { sellerId, sellers } = useUIStore();
  const { subscribe } = useSSEStore();
  const [data, setData] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const pag = usePagination('pub-queue');
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const [settingsOpen, setSettingsOpen] = useState(false);

  // Sites disponibles para este seller (verdad: sellers.active_sites)
  const sellerActiveSites: string[] = sellers.find(s => s.id === sellerId)?.active_sites || [];

  // Quick enqueue state — default: todos los active_sites del seller marcados
  const [enqueueAsins, setEnqueueAsins] = useState('');
  const [enqueueSites, setEnqueueSites] = useState<Record<string, boolean>>({});
  const [enqueuing, setEnqueuing] = useState(false);
  const [enqueueResult, setEnqueueResult] = useState<{ queued: number; duplicates: number } | null>(null);
  // Motor de armado: standard (actual) | claude (agente Sonnet: categoría+atributos)
  const [claudeMode, setClaudeMode] = useState(() => localStorage.getItem('cbt_publisher_claude_mode') === 'true');
  const toggleClaudeMode = () => setClaudeMode(prev => {
    const nv = !prev;
    localStorage.setItem('cbt_publisher_claude_mode', nv ? 'true' : 'false');
    return nv;
  });
  const [variationSiblings, setVariationSiblings] = useState<any[] | null>(null);

  // Sync checkboxes con active_sites del seller actual (todos marcados por defecto)
  useEffect(() => {
    if (sellerActiveSites.length === 0) return;
    setEnqueueSites(prev => {
      const next: Record<string, boolean> = {};
      for (const s of sellerActiveSites) next[s] = prev[s] ?? true;
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerActiveSites.join(',')]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        seller_id: String(sellerId),
        page: String(pag.page),
        per_page: String(pag.perPage),
      });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (search) params.set('search', search);

      const [dataRes, statsRes] = await Promise.all([
        api.get<any>(`/publisher/queue?${params}`),
        api.get<any>(`/publisher/queue/stats?seller_id=${sellerId}`),
      ]);
      if (dataRes.success && dataRes.data) {
        setData(Array.isArray(dataRes.data) ? dataRes.data : dataRes.data.items || []);
        pag.setTotal((dataRes as any).meta?.total || dataRes.data?.total || 0);
      }
      if (statsRes.success && statsRes.data) setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to load queue:', err);
    } finally {
      setLoading(false);
    }
  }, [sellerId, pag.page, pag.perPage, statusFilter, search]);

  useEffect(() => { loadData(); }, [loadData]);

  // SSE real-time updates (V1 parity: inline update or refresh)
  const loadRef = useRef(loadData);
  loadRef.current = loadData;
  const dataRef = useRef(data);
  dataRef.current = data;
  useEffect(() => {
    const refresh = () => loadRef.current();
    const inlineQueueStatus = (evt: any) => {
      const items = dataRef.current;
      const idx = items.findIndex((i: any) => i.id === evt.queue_id);
      if (idx >= 0) {
        const updated = [...items];
        updated[idx] = { ...updated[idx], status: evt.status };
        setData(updated);
      }
    };
    const unsubs = [
      subscribe('queue:status', inlineQueueStatus),
      subscribe('draft:published', refresh),
      subscribe('draft:created', refresh),
      subscribe('publisher:queue_updated', refresh),
      subscribe('publisher:item_published', refresh),
    ];
    return () => unsubs.forEach(u => u());
  }, [subscribe]);

  const handleEnqueue = async () => {
    if (!enqueueAsins.trim()) return;
    setEnqueuing(true);
    setEnqueueResult(null);
    setVariationSiblings(null);
    try {
      const asins = enqueueAsins
        .split(/[\n,]+/)
        .map(a => a.trim().toUpperCase())
        .filter(a => /^[A-Z0-9]{10}$/.test(a));
      const sites = Object.entries(enqueueSites)
        .filter(([, v]) => v)
        .map(([k]) => k);
      const res = await api.post<any>('/publisher/queue/enqueue', {
        asins,
        sites,
        seller_id: sellerId,
        engine: claudeMode ? 'claude' : 'standard',
      });
      if (res.success && res.data) {
        setEnqueueResult({
          queued: res.data.queued || 0,
          duplicates: res.data.duplicates || 0,
        });
        if (res.data.variation_siblings?.length) {
          setVariationSiblings(res.data.variation_siblings);
        }
        setEnqueueAsins('');
        loadData();
      }
    } catch (err) {
      console.error('Enqueue failed:', err);
    } finally {
      setEnqueuing(false);
    }
  };

  const handleRetry = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    await api.post(`/publisher/queue/${id}/retry`, {
      seller_id: sellerId,
      engine: claudeMode ? 'claude' : 'standard',
    });
    loadData();
  };

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    await api.delete(`/publisher/queue/${id}?seller_id=${sellerId}`);
    loadData();
  };

  const statuses = ['all', 'pending', 'enriching', 'ready', 'review', 'skipped', 'error', 'out_of_stock', 'pre_order', 'published', 'closed', 'paused', 'refurbished'];

  const handleEnrich = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    try {
      await api.post('/publisher/queue/enrich', {
        ids: [id],
        seller_id: sellerId,
        engine: claudeMode ? 'claude' : 'standard',
      });
      loadData();
    } catch (err) {
      console.error('Enrich failed:', err);
    }
  };

  const handlePause = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    try {
      await api.post('/publisher/queue/bulk-pause', { ids: [id] });
      loadData();
    } catch (err) {
      console.error('Pause failed:', err);
    }
  };

  const handleResume = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    try {
      await api.post('/publisher/queue/bulk-resume', { ids: [id] });
      loadData();
    } catch (err) {
      console.error('Resume failed:', err);
    }
  };

  const PAUSABLE = ['pending', 'ready', 'error', 'review', 'pre_order', 'out_of_stock'];

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === data.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(data.map(d => d.id)));
    }
  };

  const bulkAction = async (action: 'reenrich' | 'delete' | 'pause' | 'resume') => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const ids = Array.from(selectedIds);
      if (action === 'reenrich') {
        await api.post('/publisher/queue/enrich', {
          ids,
          seller_id: sellerId,
          engine: claudeMode ? 'claude' : 'standard',
        });
      } else if (action === 'delete') {
        await api.post('/publisher/queue/bulk-delete', { ids });
      } else if (action === 'pause') {
        await api.post('/publisher/queue/bulk-pause', { ids });
      } else if (action === 'resume') {
        await api.post('/publisher/queue/bulk-resume', { ids });
      }
      setSelectedIds(new Set());
      loadData();
    } catch (err) {
      console.error(`Bulk ${action} failed:`, err);
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Enrichment Progress */}
      <EnrichmentProgressPanel />

      {/* Settings gear — ajustes globales del publicador */}
      <Can cap="publisher.settings">
      <div className="flex justify-end relative">
        <button
          onClick={() => setSettingsOpen(v => !v)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
            settingsOpen
              ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]'
              : 'text-[var(--color-text-secondary)] border-[var(--color-border)] hover:text-[var(--color-text)] hover:bg-[var(--color-bg-secondary)]'
          }`}
        >
          <Settings2 size={14} />
          Settings
        </button>
        <PublishSettingsPanel open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      </div>
      </Can>

      {/* Quick Enqueue Bar */}
      <Can cap="publisher.enqueue">
      <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <textarea
            value={enqueueAsins}
            onChange={(e) => setEnqueueAsins(e.target.value)}
            placeholder={t('pub.enqueuePlaceholder')}
            rows={2}
            className="flex-1 px-3 py-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)] font-mono resize-none"
          />
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-2">
              {sellerActiveSites.map(site => (
                <label key={site} className="flex items-center gap-1 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enqueueSites[site] || false}
                    onChange={(e) => setEnqueueSites(prev => ({ ...prev, [site]: e.target.checked }))}
                    className="rounded border-[var(--color-border)]"
                  />
                  <span className="text-[var(--color-text-secondary)]">{SITE_FLAGS[site] || ''} {site}</span>
                </label>
              ))}
            </div>
            <label
              className="flex items-center gap-2 text-xs cursor-pointer select-none"
              title={claudeMode ? 'Motor Claude: el bot arma categoría y atributos' : 'Motor actual'}
              onClick={toggleClaudeMode}
            >
              <span className="text-[var(--color-text-tertiary)]">Claude</span>
              <span
                className={`w-8 h-4 rounded-full transition-colors relative ${claudeMode ? 'bg-purple-600' : 'bg-[var(--color-border)]'}`}
              >
                <span
                  className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${claudeMode ? 'left-[18px]' : 'left-0.5'}`}
                />
              </span>
            </label>
            <button
              onClick={handleEnqueue}
              disabled={enqueuing || !enqueueAsins.trim()}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-colors"
            >
              {enqueuing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {t('pub.enqueue')}
            </button>
          </div>
        </div>
        {enqueueResult && (
          <div className="mt-2 text-sm text-[var(--color-text-secondary)]">
            <span className="text-green-500 font-medium">{enqueueResult.queued}</span> {t('pub.queued')}
            {enqueueResult.duplicates > 0 && (
              <>, <span className="text-yellow-500 font-medium">{enqueueResult.duplicates}</span> {t('pub.duplicatesSkipped')}</>
            )}
          </div>
        )}
        {variationSiblings && variationSiblings.length > 0 && (
          <div className="mt-3 p-3 rounded-lg border border-blue-500/30 bg-blue-500/5">
            <div className="text-sm font-medium text-blue-400 mb-2">Variaciones detectadas</div>
            {variationSiblings.map((info: any) => (
              <div key={info.asin} className="mb-2">
                <div className="text-xs text-[var(--color-text-muted)] mb-1">
                  {info.asin} tiene {info.siblings.length} variaciones (parent: {info.parent_asin})
                </div>
                <div className="flex flex-wrap gap-1">
                  {info.siblings.filter((s: any) => !s.published && !s.in_queue).map((s: any) => (
                    <span key={s.asin} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]">
                      {s.asin} {s.color && <span className="text-[var(--color-text-muted)]">({s.color})</span>}
                    </span>
                  ))}
                </div>
                {info.siblings.some((s: any) => !s.published && !s.in_queue) && (
                  <button
                    className="mt-1 text-xs text-blue-400 hover:text-blue-300 underline"
                    onClick={async () => {
                      const sibAsins = info.siblings.filter((s: any) => !s.published && !s.in_queue).map((s: any) => s.asin);
                      const sites = Object.entries(enqueueSites).filter(([, v]) => v).map(([k]) => k);
                      await api.post('/publisher/queue/enqueue', { asins: sibAsins, sites, seller_id: sellerId });
                      setVariationSiblings(null);
                      loadData();
                    }}
                  >
                    Encolar {info.siblings.filter((s: any) => !s.published && !s.in_queue).length} variaciones faltantes
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      </Can>

      {/* Pipeline Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label={t('pub.pending')} value={stats.pending || 0} icon={<Clock size={16} />} color="yellow" />
          <StatCard label={t('pub.enriching')} value={stats.enriching || 0} icon={<Sparkles size={16} />} color="blue" />
          <StatCard label={t('pub.ready')} value={stats.ready || 0} icon={<CheckCircle2 size={16} />} color="cyan" />
          <StatCard label={t('pub.published24h')} value={stats.published_24h || 0} icon={<Rocket size={16} />} color="green" />
          <StatCard label={t('pub.errors')} value={stats.errors || 0} icon={<XCircle size={16} />} color="red" />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-0 sm:min-w-[200px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-tertiary)]" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); pag.resetPage(); }}
            placeholder={`${t('common.search')} ASIN, ${t('common.name').toLowerCase()}...`}
            className="w-full pl-9 pr-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); pag.resetPage(); }}
          className="px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
        >
          {statuses.map(s => (
            <option key={s} value={s}>
              {s === 'all' ? t('amz.allStatuses') : s.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </div>

      {/* Queue Table */}
      <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="text-left px-3 py-3">
                {canBulk && (
                  <input
                    type="checkbox"
                    checked={data.length > 0 && selectedIds.size === data.length}
                    onChange={toggleAll}
                    className="rounded border-[var(--color-border)]"
                  />
                )}
              </th>
              <th className="text-left px-4 py-3 text-[var(--color-text-secondary)] font-medium">Img</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">ASIN</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('common.name')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('common.status')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('pub.source')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('pub.sites')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('common.date')}</th>
              <th className="text-center px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-12">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-[var(--color-primary)]" />
              </td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-12 text-[var(--color-text-tertiary)]">{t('pub.noItems')}</td></tr>
            ) : data.map((row) => (
              <tr
                key={row.id}
                onClick={() => setSelectedItem(row)}
                className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)] cursor-pointer transition-colors"
              >
                <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                  {canBulk && (
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.id)}
                      onChange={() => toggleSelect(row.id)}
                      className="rounded border-[var(--color-border)]"
                    />
                  )}
                </td>
                <td className="px-4 py-3">
                  {row.thumbnail || row.product_image ? (
                    <img src={row.thumbnail || row.product_image} alt="" className="w-10 h-10 rounded object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-[var(--color-bg-tertiary)] flex items-center justify-center">
                      <Package size={16} className="text-[var(--color-text-tertiary)]" />
                    </div>
                  )}
                </td>
                <td className="px-3 py-3">
                  <EntityLink type="asin" value={row.asin} />
                </td>
                <td className="px-3 py-3 text-[var(--color-text)] max-w-[250px]">
                  <span className="block" title={row.preview_title || row.title}>{row.preview_title || row.title || '—'}</span>
                </td>
                <td className="px-3 py-3">
                  <StatusBadge status={QUEUE_STATUS_MAP[row.status] || 'unknown'} label={row.status?.replace(/_/g, ' ')} />
                </td>
                <td className="px-3 py-3">
                  {row.source && (
                    <span className="px-2 py-0.5 bg-[var(--color-bg-tertiary)] rounded text-xs text-[var(--color-text-secondary)]">
                      {row.source}
                    </span>
                  )}
                </td>
                <td className="px-3 py-3">
                  <div className="flex gap-1">
                    {(row.sites || []).map((site: string) => (
                      <span key={site} className="text-xs" title={site}>
                        {SITE_FLAGS[site] || site}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-3 text-[var(--color-text-secondary)] text-xs whitespace-nowrap">
                  {(row.updated_at || row.requested_at) ? new Date(row.updated_at || row.requested_at).toLocaleString() : '—'}
                </td>
                <td className="px-3 py-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    {canEnqueue && (row.status === 'pending' || row.status === 'error') && (
                      <button
                        onClick={(e) => handleEnrich(e, row.id)}
                        className="p-1.5 rounded hover:bg-[var(--color-bg-tertiary)] text-blue-500"
                        title={t('pub.enrich')}
                      >
                        <Sparkles size={14} />
                      </button>
                    )}
                    {canEnqueue && ['error', 'skipped', 'ready', 'review', 'pre_order', 'out_of_stock'].includes(row.status) && (
                      <button
                        onClick={(e) => handleRetry(e, row.id)}
                        className="p-1.5 rounded hover:bg-[var(--color-bg-tertiary)] text-[var(--color-primary)]"
                        title={t('pub.retry')}
                      >
                        <RefreshCw size={14} />
                      </button>
                    )}
                    {canEnqueue && PAUSABLE.includes(row.status) && (
                      <button
                        onClick={(e) => handlePause(e, row.id)}
                        className="p-1.5 rounded hover:bg-[var(--color-bg-tertiary)] text-amber-500"
                        title={t('pub.pause', 'Pausar')}
                      >
                        <Pause size={14} />
                      </button>
                    )}
                    {canEnqueue && row.status === 'paused' && (
                      <button
                        onClick={(e) => handleResume(e, row.id)}
                        className="p-1.5 rounded hover:bg-[var(--color-bg-tertiary)] text-green-500"
                        title={t('pub.resume', 'Reanudar')}
                      >
                        <Play size={14} />
                      </button>
                    )}
                    {canDelete && row.status !== 'published' && (
                      <button
                        onClick={(e) => handleDelete(e, row.id)}
                        className="p-1.5 rounded hover:bg-[var(--color-bg-tertiary)] text-[var(--color-error)]"
                        title={t('common.delete')}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        page={pag.page}
        totalPages={pag.totalPages}
        total={pag.total}
        perPage={pag.perPage}
        onPageChange={pag.setPage}
        onPerPageChange={pag.setPerPage}
      />

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && canBulk && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-xl shadow-xl px-6 py-3 flex items-center gap-4">
          <span className="text-sm text-[var(--color-text)] font-medium">
            {selectedIds.size} {t('pub.selected')}
          </span>
          <div className="flex items-center gap-2">
            <Can cap="publisher.enqueue">
            <button
              onClick={() => bulkAction('reenrich')}
              disabled={bulkLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {bulkLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              Re-Enrich
            </button>
            <button
              onClick={() => bulkAction('pause')}
              disabled={bulkLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
            >
              <Pause size={14} />
              {t('pub.pause', 'Pausar')}
            </button>
            <button
              onClick={() => bulkAction('resume')}
              disabled={bulkLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
            >
              <Play size={14} />
              {t('pub.resume', 'Reanudar')}
            </button>
            </Can>
            <Can cap="publisher.delete">
            <button
              onClick={() => bulkAction('delete')}
              disabled={bulkLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            >
              <Trash2 size={14} />
              {t('common.delete')}
            </button>
            </Can>
          </div>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="p-1.5 rounded hover:bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Queue Detail Modal */}
      {selectedItem && (
        <QueueDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} />
      )}
    </div>
  );
}

function QueueDetailModal({ item, onClose }: { item: any; onClose: () => void }) {
  const { t } = useTranslation();
  const can = useCan();
  const canMoney = can('global.view_money');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-[var(--color-bg)] rounded-xl border border-[var(--color-border)] w-full max-w-lg max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <h3 className="font-bold text-[var(--color-text)]">{t('pub.queueDetail')}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--color-bg-tertiary)]">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="flex gap-4">
            {item.thumbnail || item.product_image ? (
              <img src={item.thumbnail || item.product_image} alt="" className="w-20 h-20 rounded-lg object-cover" />
            ) : (
              <div className="w-20 h-20 rounded-lg bg-[var(--color-bg-tertiary)] flex items-center justify-center">
                <Package size={24} className="text-[var(--color-text-tertiary)]" />
              </div>
            )}
            <div>
              <p className="font-medium text-[var(--color-text)]">{item.title || item.asin}</p>
              <div className="flex items-center gap-2 mt-1">
                <EntityLink type="asin" value={item.asin} />
              </div>
              <div className="mt-1">
                <StatusBadge status={QUEUE_STATUS_MAP[item.status] || 'unknown'} label={item.status?.replace(/_/g, ' ')} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <DetailRow label={t('pub.source')} value={item.source || '—'} />
            <DetailRow label={t('pub.sites')} value={
              (item.sites || []).map((s: string) => `${SITE_FLAGS[s] || ''} ${s}`).join(', ') || '—'
            } />
            {canMoney && item.price && <DetailRow label={t('common.price')} value={`$${Number(item.price).toFixed(2)}`} />}
            {item.category && <DetailRow label={t('ai.category')} value={item.category} />}
            <DetailRow label={t('common.date')} value={item.created_at ? new Date(item.created_at).toLocaleString() : '—'} />
            {item.updated_at && <DetailRow label="Actualizado" value={new Date(item.updated_at).toLocaleString()} />}
          </div>

          {item.error_message && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-700 dark:text-red-400">{item.error_message}</p>
            </div>
          )}

          {item.skip_reason && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
              <p className="text-sm text-yellow-700 dark:text-yellow-400">{item.skip_reason}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <span className="text-[var(--color-text-tertiary)] text-xs">{label}</span>
      <div className="text-[var(--color-text)] text-sm">{value}</div>
    </div>
  );
}

export { QueueTab };
